import React, { useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';

const DraggableSection = ({ id, index, onReorder, children }) => {
  const ref = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropPosition, setDropPosition] = useState(null); // 'above' | 'below' | null

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ index }));
    // Make the entire section the drag image
    if (ref.current) {
      e.dataTransfer.setDragImage(ref.current, 0, 0);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDropPosition(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;

    const midY = rect.top + rect.height / 2;
    setDropPosition(e.clientY < midY ? 'above' : 'below');
  };

  const handleDragLeave = () => {
    setDropPosition(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDropPosition(null);

    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const fromIndex = data.index;

      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;

      const midY = rect.top + rect.height / 2;
      let toIndex = e.clientY < midY ? index : index + 1;

      // Adjust if dragging downward (the source is removed first, shifting indices)
      if (fromIndex < toIndex) {
        toIndex -= 1;
      }

      if (fromIndex !== toIndex) {
        onReorder(fromIndex, toIndex);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  return (
    <div
      ref={ref}
      className="draggable-section-wrapper"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        position: 'relative',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Drop indicator line — above */}
      {dropPosition === 'above' && (
        <div style={{
          position: 'absolute',
          top: '-2px',
          left: 0,
          right: 0,
          height: '3px',
          background: '#4f46e5',
          borderRadius: '2px',
          zIndex: 10,
          boxShadow: '0 0 6px rgba(79, 70, 229, 0.5)',
        }} />
      )}

      {/* Drag handle + content */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0,
        }}
      >
        <div
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          className="drag-handle"
          title="Drag to reorder"
          style={{
            position: 'absolute',
            left: '-36px',
            top: '4px',
            cursor: 'grab',
            padding: '4px 2px',
            borderRadius: '4px',
            color: '#9ca3af',
            opacity: 0,
            transition: 'opacity 0.2s ease, color 0.2s ease, background 0.15s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
          }}
        >
          <GripVertical size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {children}
        </div>
      </div>

      {/* Drop indicator line — below */}
      {dropPosition === 'below' && (
        <div style={{
          position: 'absolute',
          bottom: '-2px',
          left: 0,
          right: 0,
          height: '3px',
          background: '#4f46e5',
          borderRadius: '2px',
          zIndex: 10,
          boxShadow: '0 0 6px rgba(79, 70, 229, 0.5)',
        }} />
      )}

      {/* Hover styles — scoped via CSS */}
      <style>{`
        .draggable-section-wrapper:hover .drag-handle {
          opacity: 1 !important;
        }
        .drag-handle:hover {
          color: #4f46e5 !important;
          background: #eef2ff;
        }
        .drag-handle:active {
          cursor: grabbing;
        }
      `}</style>
    </div>
  );
};

export default DraggableSection;
